import { can } from "./can";
import { ORG_ROLE_PERMISSIONS } from "./roles";
import type { Permission } from "./statement";
import {
  isOrgAdmin,
  isUnrestricted,
  platformRoleOf,
  type FacilityMembership,
  type PolicySubject,
} from "./subject";

const STAFF_BASELINE = new Set<string>(ORG_ROLE_PERMISSIONS.staff);

/** The scope contract, which hundreds of queries depend on: `null` is unrestricted (no WHERE), `[]`
 * reaches nothing (return nothing, never everything), `[...]` constrains to those facility ids. */
export type FacilityIds = readonly string[] | null;

export function accessibleFacilityIds(subject: PolicySubject): FacilityIds {
  if (isUnrestricted(subject)) return null;
  const organization = subject.organization;
  if (!organization || organization.status === "suspended") return [];
  // A platform member inside a tenant reaches its every building, whatever
  // their role lets them do there.
  if (isOrgAdmin(subject) || platformRoleOf(subject)) return organization.facilityIds;
  const orgFacilities = new Set(organization.facilityIds);
  /* A building row is in scope when it holds the admin role, a role there, or an org role granting
   * more than the essentials. A staff row with no role shows the building and opens none of its
   * records. */
  const orgGrants = organization.permissions.some((p) => !STAFF_BASELINE.has(p));
  return Object.entries(subject.facilities)
    .filter(
      ([id, membership]) =>
        orgFacilities.has(id) &&
        !membership.frontDeskOnly &&
        (orgGrants || membership.role === "admin" || membership.grants.length > 0),
    )
    .map(([id]) => id);
}

/** The buildings in scope where the caller holds `permission`: holding any role at a building is
 * not holding this one there (a caregiver at B who is front desk at A sees A's calls, none of B's).
 * `null` exactly when `accessibleFacilityIds` says so. */
export function facilityScopeFor(subject: PolicySubject, permission: Permission): FacilityIds {
  const accessible = accessibleFacilityIds(subject);
  if (accessible === null) return null;
  return accessible.filter((facilityId) => can(subject, permission, { facilityId }));
}

/** The person with their front desk roster buildings in scope, for the front desk's own procedures
 * only: elsewhere a roster-only building stays out of scope, so the roster does not open its
 * residents and charts. */
export function withFrontDeskReach(subject: PolicySubject): PolicySubject {
  const entries = Object.entries(subject.facilities);
  if (!entries.some(([, membership]) => membership.frontDeskOnly)) return subject;
  const facilities: Record<string, FacilityMembership> = {};
  for (const [id, { frontDeskOnly: _roster, ...membership }] of entries) {
    facilities[id] = membership;
  }
  return { ...subject, facilities };
}

/** The caller as a procedure behind `permission` sees them: only the buildings where they hold
 *  it, so "a building they work at" in a service never reaches one where they hold another job. */
export function scopedToPermission(subject: PolicySubject, permission: Permission): PolicySubject {
  if (isUnrestricted(subject) || !subject.organization) return subject;
  const entries = Object.entries(subject.facilities);
  const kept = entries.filter(([facilityId]) => can(subject, permission, { facilityId }));
  if (kept.length === entries.length) return subject;
  return { ...subject, facilities: Object.fromEntries(kept) };
}

export type FacilityScope = { forbidden: true } | { forbidden?: false; facilityIds: FacilityIds };

/** Resolves a requested facility (`undefined`, `"all"` or an id) against what the caller may reach:
 * "all" means all of mine. With `permission`, only buildings where they hold it. Aria and MCP tools
 * reach this without `requirePermission`. */
export function resolveFacilityScope(
  subject: PolicySubject,
  requested: string | null | undefined,
  permission?: Permission,
): FacilityScope {
  const accessible = accessibleFacilityIds(subject);
  const wantsAll = !requested || requested === "all";
  if (accessible === null) {
    return { facilityIds: wantsAll ? null : [requested] };
  }
  if (wantsAll) {
    return { facilityIds: permission ? facilityScopeFor(subject, permission) : accessible };
  }
  if (!accessible.includes(requested)) return { forbidden: true };
  if (permission && !can(subject, permission, { facilityId: requested }))
    return { forbidden: true };
  return { facilityIds: [requested] };
}

/** Single-record convenience: may the caller touch a row that lives in this facility? */
export function canAccessFacility(
  subject: PolicySubject,
  facilityId: string | null | undefined,
): boolean {
  const accessible = accessibleFacilityIds(subject);
  if (accessible === null) return true;
  if (!facilityId) return false;
  return accessible.includes(facilityId);
}
